import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { connectSocket } from '../utils/socket';
import api, { chatApi } from '../utils/api';
import {
  getUnseenOpenAuditionsCount,
  subscribeToOpenAuditionsViewed,
} from '../utils/openAuditions';
import './Home.css';

interface Story {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

interface Group {
  id: number;
  title: string;
  updated_at: string;
  is_active: boolean;
  group_created_at: string;
  role_type?: string;
  role_name?: string;
  character_roles?: string[];
  crew_roles?: string[];
  extra_roles?: string[];
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stories, setStories] = useState<Story[]>([]);
  const [ownedGroups, setOwnedGroups] = useState<Group[]>([]);
  const [memberGroups, setMemberGroups] = useState<Group[]>([]);

  const [receivedRequestsCount, setReceivedRequestsCount] = useState(0);
  const [connectionRequestsCount, setConnectionRequestsCount] = useState(0);
  const [unreadDMCount, setUnreadDMCount] = useState(0);
  const [groupUnreadCounts, setGroupUnreadCounts] = useState<Record<number, number>>({});
  const [openAuditionsCount, setOpenAuditionsCount] = useState(0);
  const [calendarCount, setCalendarCount] = useState(0);
  const [loadingStories, setLoadingStories] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && !user.profileCompleted) {
      navigate('/complete-profile');
    } else {
      loadStories();
      loadGroups();
      loadRequestsCount();
      loadConnectionRequestsCount();
      loadUnreadDMCount();
      loadGroupUnreadCounts();
      loadOpenAuditionsCount();
      loadCalendarNotificationsCount();
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.profileCompleted) return undefined;

    const timer = window.setInterval(() => {
      loadCalendarNotificationsCount();
    }, 20000);

    return () => window.clearInterval(timer);
  }, [user?.profileCompleted]);

  useEffect(() => {
    return subscribeToOpenAuditionsViewed(() => {
      setOpenAuditionsCount(0);
    });
  }, []);

  // ── Real-time badge updates ────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = connectSocket(token);

    const handleNewDm = (message: any) => {
      if (message.receiver_id !== user.id) return;
      setUnreadDMCount(prev => prev + 1);
    };

    const handleNewGroupMessage = (message: any) => {
      if (message.user_id === user.id) return;
      setGroupUnreadCounts(prev => ({
        ...prev,
        [message.story_id]: (prev[message.story_id] ?? 0) + 1,
      }));
    };

    const handleNewCustomGroupMessage = (message: any) => {
      if (message.user_id === user.id) return;
      setUnreadDMCount(prev => prev + 1);
    };

    socket.on('new-direct-message', handleNewDm);
    socket.on('new-message', handleNewGroupMessage);
    socket.on('new-custom-group-message', handleNewCustomGroupMessage);

    return () => {
      socket.off('new-direct-message', handleNewDm);
      socket.off('new-message', handleNewGroupMessage);
      socket.off('new-custom-group-message', handleNewCustomGroupMessage);
    };
  }, [user?.id]);

  const loadStories = async () => {
    try {
      const response = await api.get('/stories');
      setStories(response.data.stories);
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setLoadingStories(false);
    }
  };

  const createUntitledTitle = (): string => {
    const existingTitles = new Set<string>([
      ...stories.map(s => s.title.toLowerCase()),
      ...ownedGroups.map(g => g.title.toLowerCase()),
      ...memberGroups.map(g => g.title.toLowerCase()),
    ]);

    let index = 1;
    while (existingTitles.has(`untitled${index}`)) {
      index += 1;
    }

    return `Untitled${index}`;
  };

  const handleCreateGroupFromHome = async () => {
    if (creatingGroup) return;
    setCreatingGroup(true);
    setError('');

    try {
      const title = createUntitledTitle();
      const createResponse = await api.post('/stories', { title });
      const newStoryId = createResponse.data.story.id;

      await api.put(`/stories/${newStoryId}/content`, {
        storyType: 'written',
        storyText: '',
        fileName: null,
      });

      await api.post(`/stories/${newStoryId}/start-group`);
      navigate(`/group/${newStoryId}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const loadGroups = async () => {
    try {
      const [ownedRes, memberRes] = await Promise.all([
        api.get('/groups'),
        api.get('/groups/member'),
      ]);

      const rawOwned: Group[] = ownedRes.data.groups;
      const rawMemberGroups = memberRes.data.groups;

      const ownedMap = new Map<number, Group>();
      for (const group of rawOwned) {
        ownedMap.set(group.id, {
          ...group,
          character_roles: [],
          crew_roles: [],
          extra_roles: [],
        });
      }

      const separateMemberMap = new Map<number, Group>();

      for (const group of rawMemberGroups) {
        if (ownedMap.has(group.id)) {
          const owned = ownedMap.get(group.id)!;
          if (group.character_roles?.length)
            owned.character_roles = [...(owned.character_roles || []), ...group.character_roles];
          else if (group.role_type === 'character' && group.role_name)
            owned.character_roles = [...(owned.character_roles || []), group.role_name];

          if (group.crew_roles?.length)
            owned.crew_roles = [...(owned.crew_roles || []), ...group.crew_roles];
          else if (group.role_type === 'crew' && group.role_name)
            owned.crew_roles = [...(owned.crew_roles || []), group.role_name];

          if (group.extra_roles?.length)
            owned.extra_roles = [...(owned.extra_roles || []), ...group.extra_roles];
          else if (group.role_type === 'extra' && group.role_name)
            owned.extra_roles = [...(owned.extra_roles || []), group.role_name];
        } else {
          if (!separateMemberMap.has(group.id)) {
            separateMemberMap.set(group.id, {
              ...group,
              character_roles: [],
              crew_roles: [],
              extra_roles: [],
            });
          }
          const existing = separateMemberMap.get(group.id)!;
          if (group.character_roles?.length)
            existing.character_roles = [...(existing.character_roles || []), ...group.character_roles];
          else if (group.role_type === 'character' && group.role_name)
            existing.character_roles = [...(existing.character_roles || []), group.role_name];

          if (group.crew_roles?.length)
            existing.crew_roles = [...(existing.crew_roles || []), ...group.crew_roles];
          else if (group.role_type === 'crew' && group.role_name)
            existing.crew_roles = [...(existing.crew_roles || []), group.role_name];

          if (group.extra_roles?.length)
            existing.extra_roles = [...(existing.extra_roles || []), ...group.extra_roles];
          else if (group.role_type === 'extra' && group.role_name)
            existing.extra_roles = [...(existing.extra_roles || []), group.role_name];
        }
      }

      setOwnedGroups(Array.from(ownedMap.values()));
      setMemberGroups(Array.from(separateMemberMap.values()));
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadRequestsCount = async () => {
    try {
      const response = await api.get('/requests/received');
      const pending = (response.data.requests || []).filter(
        (r: any) => r.status === 'pending'
      );
      setReceivedRequestsCount(pending.length);
    } catch (error) {
      console.error('Failed to load requests count:', error);
    }
  };

  const loadConnectionRequestsCount = async () => {
    try {
      const response = await api.get('/connections/requests');
      const pending = (response.data.requests || []).filter(
        (r: any) => r.status === 'pending'
      );
      setConnectionRequestsCount(pending.length);
    } catch (error) {
      console.error('Failed to load connection requests count:', error);
    }
  };

  const loadUnreadDMCount = async () => {
    try {
      const [dmRes, customGroupsRes] = await Promise.all([
        chatApi.get('/messages/unread-count'),
        chatApi.get('/custom-groups/unread-counts').catch(() => ({ data: { counts: {} } })),
      ]);
      const customCounts: Record<number, number> = customGroupsRes.data?.counts || {};
      const customUnread = Object.values(customCounts).reduce((sum, value) => sum + Number(value || 0), 0);
      setUnreadDMCount(Number(dmRes.data.unread_count || 0) + customUnread);
    } catch (error) {
      console.error('Failed to load unread DM count:', error);
    }
  };

  const loadGroupUnreadCounts = async () => {
    try {
      const response = await chatApi.get('/chat/unread-counts');
      setGroupUnreadCounts(response.data.counts || {});
    } catch (error) {
      console.error('Failed to load group unread counts:', error);
    }
  };

  const loadOpenAuditionsCount = async () => {
    try {
      const response = await api.get('/public-auditions?includeMedia=false');
      setOpenAuditionsCount(getUnseenOpenAuditionsCount(response.data.auditions || []));
    } catch (error) {
      console.error('Failed to load open auditions count:', error);
    }
  };

  const loadCalendarNotificationsCount = async () => {
    try {
      const response = await api.get('/schedules/calendar/unread-count');
      setCalendarCount(Number(response.data?.unreadCount || 0));
    } catch (error) {
      console.error('Failed to load calendar count:', error);
    }
  };

  const handleLogout = async () => { await logout(); };

  if (!user?.profileCompleted) {
    return <div className="home-container"><div className="loading">Checking profile...</div></div>;
  }

  const allGroups = [...ownedGroups, ...memberGroups];

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>PROJECTS</h1>

        {/* Desktop nav */}
        <div className="header-actions">
          <button onClick={() => navigate('/content')} className="content-btn">
            Home
          </button>
          <button onClick={() => navigate('/user-search')} className="find-people-btn">
            Find People
          </button>
          <span className="btn-badge-wrap">
            <button onClick={() => navigate('/open-auditions')} className="open-auditions-btn">
              Auditions
            </button>
            {openAuditionsCount > 0 && (
              <span className="notification-badge">{openAuditionsCount}</span>
            )}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => navigate('/calendar')} className="content-btn">
              Calendar
            </button>
            {calendarCount > 0 && (
              <span className="notification-badge">{calendarCount}</span>
            )}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => navigate('/direct-messages')} className="chat-btn-home">
              Chats
            </button>
            {unreadDMCount > 0 && (
              <span className="notification-badge">{unreadDMCount}</span>
            )}
          </span>
          <span className="btn-badge-wrap">
            <button onClick={() => navigate('/requests')} className="requests-btn">
              Requests
            </button>
            {(receivedRequestsCount + connectionRequestsCount) > 0 && (
              <span className="notification-badge">
                {receivedRequestsCount + connectionRequestsCount}
              </span>
            )}
          </span>
          <button onClick={() => navigate('/profile')} className="profile-btn">
            View Profile
          </button>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>

        {/* Hamburger */}
        {/* Hamburger */}
<span className="btn-badge-wrap">
  <button
    className={`hamburger-btn${menuOpen ? ' is-open' : ''}`}
    onClick={() => setMenuOpen(prev => !prev)}
    aria-label="Menu"
  >
    <span />
    <span />
    <span />
  </button>
  {(unreadDMCount + receivedRequestsCount + connectionRequestsCount + openAuditionsCount + calendarCount) > 0 && (
    <span className="notification-badge">
      {unreadDMCount + receivedRequestsCount + connectionRequestsCount + openAuditionsCount + calendarCount}
    </span>
  )}
</span>
      </div>

      {/* Mobile drawer */}
      <div className={`mobile-nav${menuOpen ? ' is-open' : ''}`}>
        <button onClick={() => { navigate('/content'); setMenuOpen(false); }} className="content-btn">
          Home
        </button>
        <button onClick={() => { navigate('/user-search'); setMenuOpen(false); }} className="find-people-btn">
          Find People
        </button>
        <span className="btn-badge-wrap">
          <button onClick={() => { navigate('/open-auditions'); setMenuOpen(false); }} className="open-auditions-btn">
            Auditions
          </button>
          {openAuditionsCount > 0 && (
            <span className="notification-badge">{openAuditionsCount}</span>
          )}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { navigate('/calendar'); setMenuOpen(false); }} className="content-btn">
            Calendar
          </button>
          {calendarCount > 0 && (
            <span className="notification-badge">{calendarCount}</span>
          )}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { navigate('/direct-messages'); setMenuOpen(false); }} className="chat-btn-home">
            Chats
          </button>
          {unreadDMCount > 0 && (
            <span className="notification-badge">{unreadDMCount}</span>
          )}
        </span>
        <span className="btn-badge-wrap">
          <button onClick={() => { navigate('/requests'); setMenuOpen(false); }} className="requests-btn">
            Requests
          </button>
          {(receivedRequestsCount + connectionRequestsCount) > 0 && (
            <span className="notification-badge">
              {receivedRequestsCount + connectionRequestsCount}
            </span>
          )}
        </span>
        <button onClick={() => { navigate('/profile'); setMenuOpen(false); }} className="profile-btn">
          View Profile
        </button>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>

      <div className="home-content">

        {/* Groups */}
        <div className="groups-section">
          <div className="section-header">
            <div>
              <h2>🎬 Groups</h2>
              {error && <p className="section-error">{error}</p>}
            </div>
            <button
              onClick={handleCreateGroupFromHome}
              disabled={creatingGroup}
              className="create-story-btn"
            >
              {creatingGroup ? 'Creating...' : 'Create Group'}
            </button>
          </div>

          {loadingGroups ? (
            <div className="loading-groups">Loading groups...</div>
          ) : allGroups.length === 0 ? (
            <div className="no-groups">
              <p>No active groups yet. Start a group from your screenplay to collaborate!</p>
            </div>
          ) : (
            <div className="groups-grid">
              {allGroups.map((group) => {
                const unreadCount = groupUnreadCounts[group.id] ?? 0;
                return (
                  <div
                    key={group.id}
                    className="group-card"
                    onClick={() => navigate(`/group/${group.id}`)}
                    style={{ position: 'relative' }}
                  >
                    {unreadCount > 0 && (
                      <span className="group-unread-badge">{unreadCount}</span>
                    )}

                    <div className="group-icon">🎬</div>
                    <h3>{group.title}</h3>

                    {(() => {
                      const allRoles: string[] = [];
                      const isOwner = ownedGroups.some(og => og.id === group.id);
                      if (isOwner) allRoles.push(`👑 Owner`);

                      if (group.character_roles?.length) group.character_roles.forEach(r => allRoles.push(`🎭 ${r}`));
                      if (group.crew_roles?.length) group.crew_roles.forEach(r => allRoles.push(`🎬 ${r}`));
                      if (group.extra_roles?.length) group.extra_roles.forEach(() => allRoles.push(`👥 Extra`));

                      if (allRoles.length === 0 && group.role_name) {
                        const icon =
                          group.role_type === 'character' ? '🎭' :
                          group.role_type === 'crew' ? '🎬' : '👥';
                        allRoles.push(`${icon} ${group.role_name}`);
                      }

                      return allRoles.length > 0 ? <p className="group-role">{allRoles.join(', ')}</p> : null;
                    })()}

                    <p className="group-date">
                      Started: {new Date(group.group_created_at).toLocaleDateString()}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stories */}
        <div className="stories-section">
          <div className="section-header">
            <h2>📝 Story Drafts</h2>
            <button onClick={() => navigate('/story/new')} className="create-story-btn">
              ➕ New Story
            </button>
          </div>

          {loadingStories ? (
            <div className="loading-stories">Loading stories...</div>
          ) : stories.length === 0 ? (
            <div className="no-stories">
              <p>No stories yet. Click "New Story" to create your first story!</p>
            </div>
          ) : (
            <div className="stories-grid">
              {stories.map((story) => (
                <div key={story.id} className="story-card" onClick={() => navigate(`/story/${story.id}`)}>
                  <h3>{story.title}</h3>
                  <p className="story-date">
                    Last updated: {new Date(story.updated_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="info-section">
          <h3>Account Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Stories:</span>
              <span className="info-value">{stories.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Active Groups:</span>
              <span className="info-value">{allGroups.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Pending Requests:</span>
              <span className="info-value">{receivedRequestsCount}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Account Status:</span>
              <span className="info-value status-active">Active</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HomePage;
